import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { mediaScreen } from '../../utils/mediaScreen';
import { OptimizedImage } from '../ui/optimizedImage';

export const RichTabs: React.FC<RichTabsProps> = ({
  items,
  moreLabel = 'More Items',
  moreIcon = '/img/icon-more.svg',
  autoplayInterval = 10000,
  disableAutoplay: disableAutoplayProp,
  className,
  maxVisibleTabs = 4,
}) => {
  const [disableAutoplay, setDisableAutoplay] = useState(!!disableAutoplayProp);

  const { visibleTabs, moreTabs } = useMemo(() => {
    if (!items) {
      return {
        visibleTabs: [],
        moreTabs: [] as RichTabsItem[],
      };
    }

    if (items.length <= maxVisibleTabs) {
      return {
        visibleTabs: items,
        moreTabs: [] as RichTabsItem[],
      };
    }

    const head = items.slice(0, maxVisibleTabs);
    const rest = items.slice(maxVisibleTabs);
    const more: RichTabsItem = { title: moreLabel, icon: moreIcon };

    return {
      visibleTabs: [...head, more],
      moreTabs: rest,
    };
  }, [items, moreLabel, moreIcon, maxVisibleTabs]);

  const [active, setActive] = useState(0);
  const [mobileOpenIndex, setMobileOpenIndex] = useState<number>(0);

  const isMore = (idx: number) => idx === maxVisibleTabs && moreTabs.length > 0;

  const onSelect = useCallback((idx: number) => {
    setDisableAutoplay(true);
    setActive(idx);
  }, []);

  useEffect(() => {
    if (disableAutoplay) {
      return;
    }

    const timer = setTimeout(() => {
      setActive((cur) => (cur + 1) % visibleTabs.length);
    }, autoplayInterval);

    return () => clearTimeout(timer);
  }, [active, autoplayInterval, disableAutoplay, visibleTabs.length]);

  const forceOpenAccordion = useCallback((idx: number) => {
    setMobileOpenIndex(idx);
  }, []);

  useEffect(() => {
    if (mobileOpenIndex !== active) {
      setMobileOpenIndex(active);
    }
  }, [active, mobileOpenIndex]);

  const activeContent = useMemo(() => {
    const tab = visibleTabs[active];

    if (!tab) {
      return null;
    }

    if (isMore(active)) {
      return moreTabs[0];
    }

    return tab;
  }, [active, visibleTabs, moreTabs, isMore]);

  return (
    <RichTabsRoot className={className}>
      <div className="rt-left">
        <ul className="rt-tabs-list" role="tablist">
          {visibleTabs.map((tab, idx) => {
            const activeState = idx === active;
            const showProgress = activeState && !disableAutoplay;
            const accordionOpen = mobileOpenIndex === idx;

            return (
              <li
                key={idx}
                className={`rt-tab ${accordionOpen ? 'rt-accordion-open' : ''}`}
              >
                <div
                  className={`rt-tab-item ${activeState ? 'is-active' : ''}`}
                  role="tab"
                  aria-selected={activeState}
                  aria-expanded={accordionOpen}
                  onClick={() => {
                    onSelect(idx);
                    forceOpenAccordion(idx);
                  }}
                >
                  {tab.icon && (
                    <img src={tab.icon} alt="" className="rt-tab-icon" />
                  )}
                  <span className="rt-tab-label">{tab.title}</span>
                  {showProgress && (
                    <div className="rt-progress" aria-hidden="true">
                      <span
                        className="rt-progress-bar"
                        style={{ animationDuration: `${autoplayInterval}ms` }}
                      />
                    </div>
                  )}
                </div>

                <div className="rt-mobile-group">
                  <div className={`rt-mobile-panel is-more-${isMore(idx)}`}>
                    {isMore(idx) ? (
                      moreTabs.map((m, mIdx) => (
                        <div key={mIdx} className="rt-mobile-item">
                          <div style={{ width: 50, height: 50 }}>
                            <OptimizedImage
                              src={m.image!}
                              alt=""
                              width={50}
                              height={50}
                            />
                          </div>

                          <div className="inner">
                            <div
                              className="rt-buttons"
                              style={{ marginTop: 8 }}
                            >
                              {m.primary_button?.href && (
                                <a href={m.primary_button.href}>
                                  {m.primary_button.title || 'Explore'} →
                                </a>
                              )}
                              {m.secondary_button?.href && (
                                <a href={m.secondary_button.href}>
                                  {m.secondary_button.title || 'Learn more'} →
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="rt-mobile-item">
                        {tab.image && (
                          <OptimizedImage
                            src={tab.image}
                            alt=""
                            width={334}
                            height={187}
                          />
                        )}

                        <div className="inner">
                          {tab.description && (
                            <div
                              className="rt-description"
                              style={{ fontSize: 12 }}
                            >
                              {tab.description}
                            </div>
                          )}
                          <div className="rt-buttons" style={{ marginTop: 8 }}>
                            {tab.primary_button?.href && (
                              <a href={tab.primary_button.href}>
                                {tab.primary_button.title} →
                              </a>
                            )}
                            {tab.secondary_button?.href && (
                              <a href={tab.secondary_button.href}>
                                {tab.secondary_button.title} →
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className={`rt-right is-more-${isMore(active)}`}>
        {isMore(active) ? (
          moreTabs?.map((tab) => (
            <div key={tab.title} className="rt-more-item">
              <div className="rt-image-wrapper">
                <OptimizedImage src={tab?.image!} width={309} height={160} />
              </div>

              <div className="rt-description" />

              <div className="rt-buttons">
                <a href={tab?.primary_button?.href!}>
                  {tab?.primary_button?.title} →
                </a>
              </div>
            </div>
          ))
        ) : (
          <>
            <div className="rt-image-wrapper">
              <OptimizedImage
                src={activeContent?.image!}
                alt=""
                width={658}
                height={330}
              />
            </div>

            <div className="rt-description">{activeContent?.description}</div>

            <div className="rt-buttons">
              <a href={activeContent?.primary_button?.href!}>
                {activeContent?.primary_button?.title} →
              </a>

              {activeContent?.secondary_button?.href && (
                <a href={activeContent.secondary_button.href}>
                  {activeContent.secondary_button.title} →
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </RichTabsRoot>
  );
};

const RichTabsRoot = styled.section`
  @keyframes rtFill {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
  position: relative;
  width: 100%;
  display: flex;
  align-items: stretch;

  ${mediaScreen('xs', 'md')} {
    flex-direction: column;
    gap: 16px;
  }

  .rt-left {
    flex: 0.5;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    ${mediaScreen('xs', 'md')} {
      background: transparent;
      flex: 1 1 auto;
      max-width: none;
    }
  }

  .rt-tabs-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }

  .rt-tab-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 32px;
    font-size: 16px;
    font-weight: 400;
    cursor: pointer;
    color: ${(p) => p.theme.colors.white};
    border-top: 2px solid ${(p) => p.theme.colors.slate2};
    user-select: none;
    overflow: hidden;
  }
  .rt-tab:last-child .rt-tab-item {
    border-bottom: 2px solid ${(p) => p.theme.colors.slate2};
  }
  .rt-tab-item:hover {
    background: #262f4052;
  }
  .rt-tab-item.is-active {
    border-color: transparent;
    background: linear-gradient(
        105deg,
        rgba(255, 150, 50, 0.15) 0%,
        rgba(50, 200, 150, 0.15) 20%,
        transparent 85%
      ),
      #262f40;
  }
  .rt-progress {
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 100%;
    background: transparent;
    pointer-events: none;
  }
  .rt-progress-bar {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    background: ${(p) => p.theme.colors.teal};
    animation: rtFill ease forwards;
  }
  .rt-tab-icon {
    width: 60px;
    display: block;
    object-fit: contain;
  }
  .rt-tab-label {
    flex: 1;
    font-size: 20px;
  }

  .rt-right {
    flex: 0.5;
    background: ${(p) => p.theme.colors.slate2};
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    padding: 32px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;

    &.is-more-true {
      flex-direction: row;
      flex-wrap: wrap;
      gap: 40px;

      .rt-more-item {
        width: calc(50% - 20px);

        .rt-image-wrapper {
          height: 160px;
        }
      }
    }
    ${mediaScreen('xs', 'md')} {
      padding: 0;
      background: transparent;
    }
  }

  .rt-image-wrapper {
    width: 100%;
    flex: 1;
    max-height: 330px;
    border-radius: 8px;
    overflow: hidden;
  }
  .rt-image-wrapper {
    div,
    img {
      height: 101%;
      max-height: 101%;
      width: 100%;
      object-fit: cover;
    }
  }

  .rt-description {
    font-size: 16px;
    line-height: 1.55;
    margin: 24px 0;
    color: ${(p) => p.theme.colors.white};
  }
  .rt-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 28px;
  }
  .rt-buttons a {
    font-size: 16px;
    text-decoration: none;
    color: ${(p) => p.theme.colors.teal};
    display: inline-flex;
    align-items: center;
    font-weight: 600;
  }
  .rt-buttons a:hover {
    text-decoration: underline;
  }
  .rt-mobile-group {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    .rt-left {
      order: 2;
      border-radius: 0 !important;
    }
    .rt-right {
      display: none;
      gap: 0;
    }
    .rt-tab-item {
      padding: 18px;
      background: transparent;
      border-top: 0;
      border-bottom: 1px solid ${(p) => p.theme.colors.slate2};
      gap: 16px;
      overflow: hidden;
      border-radius: 0 !important;
    }
    .rt-progress {
      height: 2px;
    }
    .rt-tabs-list {
      gap: 0;
    }
    .rt-mobile-group {
      display: block;
    }
    .rt-mobile-panel {
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      will-change: max-height, opacity, transform;
      background: ${(p) => p.theme.colors.slate2};
      border-radius: 0;
      padding: 0 0 0 0;
    }
    .rt-accordion-open .rt-mobile-panel {
      max-height: 800px;
      opacity: 1;
    }
    .rt-tab-icon {
      width: 40px;
    }

    .rt-tab-label {
      font-size: 16px;
    }
    .rt-description {
      font-size: 14px !important;
      margin-top: 0;
    }
    .rt-buttons {
      margin-top: 0 !important;

      a {
        font-size: 14px;
      }
    }
    .rt-mobile-item {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 16px;
    }
    .rt-mobile-item {
      img,
      div {
        width: 100%;
        height: 100%;
        border-radius: 8px;
        object-fit: cover;
      }
    }
    .rt-mobile-item .inner {
      flex: 1;
    }
    .rt-accordion-open .rt-tab-item {
      background: linear-gradient(
          105deg,
          rgba(255, 150, 50, 0.15) 0%,
          rgba(50, 200, 150, 0.15) 20%,
          transparent 85%
        ),
        #262f40;
    }
    .rt-tab-item:after {
      content: '';
      position: absolute;
      right: 16px;
      top: 50%;
      width: 24px;
      height: 24px;
      transform: translateY(-50%) rotate(0deg);
      transition: transform 0.4s ease;
      background: ${(p) => p.theme.colors.teal};
      -webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' stroke='white' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")
        center/24px 24px no-repeat;
      mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' stroke='white' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")
        center/24px 24px no-repeat;
      opacity: 0.6;
    }
    .rt-accordion-open > .rt-tab-item:after {
      transform: translateY(-50%) rotate(180deg);
      opacity: 1;
    }

    .rt-mobile-panel.is-more-true {
      display: flex;
      flex-direction: column;
      padding-bottom: 8px;

      .rt-mobile-item {
        display: flex;
        flex-direction: row;
        gap: 16px;
        align-items: center;
        padding: 8px 16px;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 3px;
        }

        a {
          font-size: 16px;
        }
      }
    }
  }
`;

type RichTabsItem = {
  title?: string | null;
  description?: string | null;
  icon?: string | null;
  image?: string | null;
  primary_button?: { title?: string | null; href?: string | null } | null;
  secondary_button?: { title?: string | null; href?: string | null } | null;
};

type RichTabsProps = {
  items: RichTabsItem[];
  moreLabel?: string;
  moreIcon?: string;
  autoplayInterval?: number;
  disableAutoplay?: boolean;
  className?: string;
  maxVisibleTabs?: number;
};
